import { lazy, Suspense, useSyncExternalStore } from 'react'
import type { ChartPoint } from 'models/chart'
import { ChartPlaceholder } from './PriceChart.styles'

export interface ChartHover {
  time: number
  value: number
}

export interface PriceChartProps {
  points: ChartPoint[]
  /** `area`: line with a fading fill; `column`: bars, e.g. monthly returns. */
  kind?: 'area' | 'column'
  /** Line/bar colour; an area's fill is a fading gradient of it. */
  color: string
  /** Bar colour below zero. */
  negativeColor?: string
  height: number
  /** Value axis on the left and date axis below (BMN2 style); off for the clean Coinbase look. */
  showAxes?: boolean
  /** The main series' name, for the tooltip when a `compare` line is drawn too. */
  name?: string
  /** A second line to compare against (BMN1 vs BTC buy and hold); the tooltip then lists both. */
  compare?: { points: ChartPoint[]; color: string; name: string }
  /** A dot on every point, for sparse series such as one value a month. */
  showMarkers?: boolean
  /** Crosshair, tooltip and `onHover`. Off for static sparklines. */
  interactive?: boolean
  /** Axis-label formatter for values. */
  formatValue?: (value: number) => string
  /** Adds the value to the tooltip, for charts without a header that follows the hover. */
  formatTooltipValue?: (value: number) => string
  /** Hovered point, or null when the pointer leaves — lets a header follow the cursor. */
  onHover?: (hover: ChartHover | null) => void
  /** Screen-reader description of what the chart shows. */
  description?: string
  /** The line draws in from left to right when the chart appears (remount it to draw again). */
  drawIn?: boolean
  /**
   * Round value-axis steps, about one per 50px (Highcharts' own leave BTC mined at 0.2 steps topping
   * out an empty band higher, at 0.6, or at 0.025 steps): for small non-negative amounts.
   */
  denseValueAxis?: boolean
}

// Highcharts needs a browser and is heavy: it loads on the client only, after hydration.
const PriceChartCanvas = lazy(() => import('./PriceChartCanvas'))

const subscribe = () => () => {}

/**
 * Reusable price/value chart. The server and the first client render show a placeholder of
 * the same height, so hydration matches and the layout does not jump when the chart loads.
 */
export const PriceChart = (props: PriceChartProps) => {
  const isClient = useSyncExternalStore(
    subscribe,
    () => true,
    () => false,
  )
  const placeholder = <ChartPlaceholder style={{ height: props.height }} aria-hidden="true" />

  if (!isClient) return placeholder
  return (
    <Suspense fallback={placeholder}>
      <PriceChartCanvas {...props} />
    </Suspense>
  )
}
